import axios from 'axios'
import { Buffer } from 'buffer/'
import React from 'react'

import { graphql } from '~/gql'
import { useCreateSharedUrl } from '~/hooks/useCreateSharedUrl'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { exists, type FigureInfo, FigureInfoHelpers } from '~/utils'

const document = graphql(`
  mutation SaveDocumentImageDialog_CreateDocumentImage(
    $brandAccountId: ID!
    $comment: String
    $title: String!
    $pathKey: String!
    $restoreKey: ID!
    $allMetadata: [DocumentImageMetadataInput!]!
  ) {
    createDocumentImage(
      brandAccountId: $brandAccountId
      comment: $comment
      title: $title
      pathKey: $pathKey
      restoreKey: $restoreKey
      allMetadata: $allMetadata
    ) {
      id
      url
    }
  }
`)

type SaveDocumentImageParams = {
  comment?: string
  figureInfo: FigureInfo
  imageData: string | null
  restoringParams: unknown
  title: string
}

export const useSaveDocumentImage = () => {
  const createSharedUrl = useCreateSharedUrl()
  const graphQLRequest = useGraphQLRequest()

  return React.useCallback(
    async ({
      comment,
      figureInfo,
      imageData,
      restoringParams,
      title,
    }: SaveDocumentImageParams): Promise<string | null> => {
      // Returns the created DocumentImage id on success (callers that only need
      // a success flag can treat the id as truthy), or null on any failure.
      if (!exists(imageData)) {
        return null
      }

      const createSharedUrlResult = await createSharedUrl(restoringParams, {
        notCopyToClipboard: true,
      })

      if (!exists(createSharedUrlResult)) {
        return null
      }

      const trimmedTitle = title.trim()

      const createDocumentImageResult = await graphQLRequest({
        document,
        variables: {
          allMetadata: FigureInfoHelpers.toMetadataArray(figureInfo),
          brandAccountId: figureInfo.brandAccountId,
          comment: comment?.trim() || null,
          pathKey: figureInfo.pathKey,
          restoreKey: createSharedUrlResult.key,
          title:
            0 < trimmedTitle.length
              ? trimmedTitle
              : (FigureInfoHelpers.toScreenLabel(figureInfo) ?? ''),
        },
      })

      const url = createDocumentImageResult.createDocumentImage?.url
      if (!exists(url)) {
        return null
      }

      const fileData = imageData.replace(/^data:\w+\/\w+;base64,/, '')
      const decodedFile = new Buffer(fileData, 'base64')

      const axiosResult = await axios.put(url, decodedFile, {
        headers: {
          'Content-Type': 'image/png',
        },
      })

      if (axiosResult.status !== 200) {
        return null
      }

      return createDocumentImageResult.createDocumentImage?.id ?? null
    },
    [createSharedUrl, graphQLRequest],
  )
}
